/* eslint-disable @typescript-eslint/no-explicit-any */
import { createMessageQueue, type MessageQueue } from './messageQueue';

interface SSERequestOptions {
  url: string;
  method?: 'GET' | 'POST';
  params?: Record<string, string | number | boolean>;
  data?: any;
  onMessage: (data: any) => void;
  onError?: (error: Event | Error) => void;
  onFinish?: () => void;
  onStart?: () => void;
  headers?: Record<string, string>;
  skipGlobalErrorHandling?: boolean;
  /** 是否启用消息队列，均匀化 onMessage 调用节奏以实现平滑打字机效果，默认 false */
  enableQueue?: boolean;
  /** 队列模式下两次 onMessage 调用的最小间隔（ms），默认 50 */
  queueInterval?: number;
}

export interface SSEConnection {
  disconnect: () => void;
  getReadyState: () => number;
}

/**
 * SSE 连接状态
 */
export const SSEState = {
  CONNECTING: 0, // 正在连接
  OPEN: 1, // 连接已打开
  CLOSED: 2, // 连接已关闭
};

type SSEEvent = { event: string; data: string; id?: string };

/**
 * 构建带查询参数的 URL
 */
function buildUrlWithParams(baseUrl: string, queryParams?: Record<string, string | number | boolean>): string {
  if (!queryParams || Object.keys(queryParams).length === 0) {
    return baseUrl;
  }

  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${searchParams.toString()}`;
}

/**
 * 解析 SSE 文本块，返回事件对象列表
 * SSE 格式：每个事件由多行组成，字段格式为 "field: value"，事件间以空行分隔
 */
function parseSSEChunk(chunk: string): SSEEvent[] {
  const events: SSEEvent[] = [];
  const eventBlocks = chunk.split(/\n\n/);

  for (const block of eventBlocks) {
    if (!block.trim()) continue;

    let eventType = 'message';
    let eventData = '';
    let eventId: string | undefined;

    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) {
        eventType = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        eventData += (eventData ? '\n' : '') + line.slice('data:'.length).trim();
      } else if (line.startsWith('id:')) {
        eventId = line.slice('id:'.length).trim();
      }
      // 忽略 retry: 和注释行（以 : 开头）
    }

    if (eventData) {
      events.push({ event: eventType, data: eventData, id: eventId });
    }
  }

  return events;
}

/**
 * 处理单个 SSE 事件，将 MESSAGE/SUB_MESSAGE 的 data 字段解析为 JSON 后交给 onMessage
 */
function handleSSEEvent(event: SSEEvent, dispatchMessage: (data: any) => void): void {
  console.log('SSE onmessage 调用', event);

  if (event.event === 'MESSAGE' || event.event === 'SUB_MESSAGE' || event.event === 'SUB_MESSAGE_CHUNK') {
    try {
      const parsedData = JSON.parse(event.data);
      dispatchMessage({ ...event, data: parsedData });
    } catch (_err) {
      console.error('SSE handle message error:', _err);
      dispatchMessage(event);
    }
  } else {
    console.log('SSE other event', event);
  }
}

/**
 * SSE 通用请求函数（原生 fetch 实现）
 * 使用原生 fetch + ReadableStream 解析 SSE 事件流
 * @param options SSE 请求选项
 * @returns SSE 连接实例，包含断开连接和获取状态的方法
 */
export function sseRequest(options: SSERequestOptions): SSEConnection {
  const {
    url,
    method = 'GET',
    params,
    data,
    onMessage,
    onError,
    onFinish,
    onStart,
    headers = {},
    skipGlobalErrorHandling = false,
    enableQueue = false,
    queueInterval = 50,
  } = options;

  let abortController: AbortController | null = null;
  let isFinished = false;
  let readyState = SSEState.CONNECTING;
  /** SSE 流是否已结束（reader.read() done 或网络断开），区别于队列是否消费完 */
  let streamEnded = false;

  /** 真正的结束回调：标记状态 + 通知调用方 */
  const finishCallback = () => {
    if (!isFinished) {
      isFinished = true;
      readyState = SSEState.CLOSED;
      onFinish?.();
      console.log('SSE 连接已完成');
    }
  };

  let messageQueue: MessageQueue<any> | null = null;
  if (enableQueue) {
    messageQueue = createMessageQueue({
      handler: onMessage,
      interval: queueInterval,
      // 队列排空完毕的回调：若 SSE 流已结束，此时才真正触发 onFinish
      onDrain: () => {
        if (streamEnded) {
          finishCallback();
        }
      },
    });
  }

  /**
   * 消息分发：启用队列时走 push（按节奏消费），否则直接调用 onMessage。
   * 所有 SSE 事件（MESSAGE / SUB_MESSAGE_CHUNK / SUB_MESSAGE）都经过此函数。
   */
  const dispatchMessage = (data: any) => {
    if (messageQueue) {
      messageQueue.push(data);
    } else {
      onMessage(data);
    }
  };

  /**
   * SSE 流结束时调用。
   * - 有队列：标记 streamEnded，让队列按原节奏继续消费剩余消息，消费完后通过 onDrain → finishCallback 结束
   * - 无队列：直接调用 finishCallback 结束
   */
  const handleFinish = () => {
    streamEnded = true;
    if (messageQueue) {
      messageQueue.startDrain();
    } else {
      finishCallback();
    }
  };

  const initConnection = async () => {
    try {
      onStart?.();

      const fullUrl = buildUrlWithParams(url, params);
      abortController = new AbortController();

      const response = await fetch(fullUrl, {
        method,
        headers: {
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/json',
          ...headers,
        },
        body: method === 'POST' && data ? JSON.stringify(data) : undefined,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE 请求失败: ${response.status} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('SSE 响应体为空');
      }

      readyState = SSEState.OPEN;
      console.log('SSE 连接已打开');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          if (buffer.trim()) {
            for (const event of parseSSEChunk(buffer)) {
              handleSSEEvent(event, dispatchMessage);
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // 按双换行符分割完整事件，保留最后一个可能不完整的块
        const lastDoubleNewline = buffer.lastIndexOf('\n\n');
        if (lastDoubleNewline !== -1) {
          const completeChunk = buffer.slice(0, lastDoubleNewline + 2);
          console.log('SSE completeChunk before', buffer);
          buffer = buffer.slice(lastDoubleNewline + 2);
          console.log('SSE completeChunk after', buffer);

          for (const event of parseSSEChunk(completeChunk)) {
            handleSSEEvent(event, dispatchMessage);
          }
        }
      }

      handleFinish();
    } catch (error) {
      // AbortError 是主动断开，不视为错误
      if ((error as Error).name === 'AbortError') {
        console.log('SSE 连接已主动断开');
        return;
      }

      console.error('SSE 连接错误:', error);
      onError?.(error as Error);

      if (!skipGlobalErrorHandling) {
        // toast.error('SSE 连接失败', { richColors: true });
      }

      handleFinish();
    }
  };

  const disconnect = () => {
    // SSE 流已结束、队列正在按节奏排空剩余消息时，忽略 abort 请求，
    // 保证用户能看到完整的打字机效果
    if (streamEnded && messageQueue?.isDraining()) {
      return;
    }
    if (abortController) {
      abortController.abort();
      abortController = null;
      readyState = SSEState.CLOSED;
      // SSE 流未结束时 abort：清空队列，丢弃未渲染的消息
      messageQueue?.clear();
      handleFinish();
      console.log('SSE disconnect');
    }
  };

  const getReadyState = () => readyState;

  initConnection();

  return { disconnect, getReadyState };
}
